/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 8/30/2023
 * for userx-support
 */

import React, {useEffect, useState} from 'react';
import CompanyForm from "./CompanyForm";
import ContactsForm from "./ContactsForm";
import {addCompany, addCompanyContact, urlExists} from "../../../_utilities/companyUtilities";
import {search} from "../../../_utilities/reactUtils";
import {addContact, sendWelcome} from "../../../_utilities/contactUtilities";
import CompanyPreferences from "../CompanyPreferences";
import supportAPI from "../../../_apis/supportAPI";
import useCompanyInfo from "../../../_hooks/useCompanyInfo";
import {useParams} from "react-router";
import useLocalStorage from "../../../_hooks/useLocalStorage";

const RegistrationForm = () => {
          const [errorMsg, setErrorMsg] = useState('');
          const [step, setStep] = useState(1);
          const [companyInfo, setCompanyInfo] = useState({name: '', url: '', description: ''});
          const [contacts, setContacts] = useState([]);
          const [prefsSaved, setPrefsSaved] = useState(false);
          const [pendingPrefs, setPendingPrefs] = useLocalStorage('uxCompanyPrefs', {readiness: 'quarterly', capabilities: ['maintenance']});
          const [pendingReview, setPendingReview] = useState(null);


          useEffect(() => {
              try {
                  const params = new URLSearchParams(window.location.search || '');
                  const readinessParam = params.get('readiness');
                  const capabilitiesParam = params.get('capabilities');
                  const reviewIdParam = params.get('review_id');

                  // Check for readiness and capabilities parameters
                  if (readinessParam || capabilitiesParam) {
                      const capabilities = capabilitiesParam
                          ? decodeURIComponent(capabilitiesParam).split(',').map(s => s.trim()).filter(Boolean)
                          : [];

                      const prefs = {
                          readiness:    readinessParam || 'quarterly',
                          capabilities: capabilities.length ? capabilities : ['maintenance']
                      };

                      // persist for CompanyPreferences (and other consumers)
                      setPendingPrefs(prefs);
                  }

                  // Check for review_id parameter
                  if (reviewIdParam) {
                      console.log('Found review_id parameter:', reviewIdParam);
                      const numericReviewId = parseInt(reviewIdParam, 10);
                      if (Number.isNaN(numericReviewId) || numericReviewId <= 0) {
                          console.error('Invalid review_id parameter: must be a positive integer, got', reviewIdParam);
                      } else {
                          supportAPI.get(`/review/${numericReviewId}/tickets`).then(response => {
                              const review = response.data;
                              console.log(review);
                              if (review) {
                                  // Pre-fill company info
                                  setCompanyInfo(prev => ({
                                      ...prev,
                                      name: review.prospect_company || prev.name,
                                      url:  review.prospect_url || prev.url,
                                      status: 2,
                                      services: 2,
                                      default_agent: 1
                                  }));

                                  // Store review info for post-registration (as string for sessionStorage)
                                  setPendingReview(review);

                                  // Track link click
                                  supportAPI.get(`/review/${numericReviewId}/link`);
                              }
                          }).catch(err => {
                              console.error('Failed to load review:', err);
                          });
                      }
                  }
              } catch (err) {
                  // ignore parsing errors
                  console.error('Registration param parse error', err);
              }
          }, []);

          const handleCompanyInfoChange = (e) => {
              const {name, value} = e.target;
              setCompanyInfo((prevInfo) => ({...prevInfo, [name]: value}));
          };

          const handleCompanySave = async () => {
              try {
                  if (!validateCompany()) throw new Error('Please fill out all fields completely.');

                  const exists = await urlExists(companyInfo.url);
                  if (exists) throw new Error('A company with this URL already exists.');

                  const response = await addCompany(companyInfo);
                  setCompanyInfo(prev => ({...prev, company_id: response.company_id}));
                  handleNextStep();
              } catch (err) {
                  setErrorMsg(err.message || 'Unexpected error occurred.');
              }
          };

          // Handle progression to next step
          // Note: This function is async to support review conversion when moving to step 4
          const handleNextStep = async () => {
              const nextStep = step + 1;

              // Check for pending review conversion when moving to step 4 (completion)
              if (nextStep === 4) {
                  if (pendingReview && pendingReview.tickets.length > 0) {
                      const pendingReviewId = parseInt(pendingReview.review_id);
                      const primaryContact = contacts.find(c => c.primary === 1 || c.primary === '1') || contacts[0];
                      try {
                          if(!pendingReviewId) throw new Error('Invalid pending review ID for conversion.');
                          if(!companyInfo.company_id) throw new Error('Company ID missing for review conversion.');
                          if(!primaryContact || primaryContact === undefined) throw new Error('No contacts found for review conversion.');

                          const convertPayload = {
                              company_id: companyInfo.company_id,
                              contact_id: primaryContact.contact_id
                          };
                          console.log('Converting pending review', pendingReviewId, 'to company', companyInfo.company_id, convertPayload);
                          await supportAPI.post(`/review/${pendingReviewId}/convert`, convertPayload);
                          sessionStorage.removeItem('pending_review_id');
                          console.log('Review tickets created successfully');
                      } catch (err) {
                          console.error('Failed to create review tickets:', {
                              error:     err.message,
                              response:  err.response?.data,
                              status:    err.response?.status,
                              reviewId:  pendingReviewId,
                              companyId: companyInfo.company_id,
                              contactId: primaryContact?.contact_id
                          });
                      }
                  }
              }

              setStep(() => nextStep);
          };

          const handlePrevStep = () => {
              const nextStep = step - 1;
              setStep(() => nextStep);
          }

          const validateCompany = () => {
              if (companyInfo.name !== '' && companyInfo.url !== '') {
                  return true;
              }
              return false;
          }

          const validateContacts = () => {
              if (contacts.length >= 1) {
                  return true;
              }
              return false;
          }

          const save = async () => {
              try {
                  const newCompany = await addCompany(companyInfo);
              } catch (err) {
                  console.error(err);
                  switch (err.response.status) {
                      case 422:
                          setErrorMsg('A company with this URL already exists.');
                          break;
                      default:
                          setErrorMsg(err.message);
                  }
              }
          };

          const renderStep = () => {
              switch (step) {
                  case 1:
                      return (<>
                          <p className={`text-center`}>Please fill out the form below to register.</p>
                          <CompanyForm companyInfo={companyInfo} onChange={handleCompanyInfoChange}/>
                          <button
                              disabled={!validateCompany()}
                              className={`btn btn-primary d-block mx-auto`}
                              type="button"
                              onClick={handleCompanySave}>
                              Add Contacts
                          </button>
                      </>);
                  case 2:
                      return (<>
                          <p className={`text-center`}>You must have at least one contact in order to register.</p>
                          <ContactsForm company_id={companyInfo.company_id} onContactsChanged={list => setContacts(prev => list)}/>
                          <div className="d-flex justify-content-center align-items-center">
                              <button
                                  className={`d-block btn btn-primary d-block mx-2`}
                                  type="button"
                                  onClick={handlePrevStep}>
                                  Back to Company Info
                              </button>
                              <button
                                  disabled={!validateContacts()}
                                  className={`btn btn-primary d-block mx-2`}
                                  type="button"
                                  onClick={handleNextStep}>
                                  Set Preferences
                              </button>
                          </div>
                      </>);
                  case 3:
                      return (
                          <>
                              <CompanyPreferences company_id={companyInfo.company_id}
                                                  pendingPrefs={pendingPrefs}
                                                  onSaved={saved => {setPrefsSaved(saved)}}/>
                              <div className="d-flex justify-content-center align-items-center">
                                  <button
                                      className={`d-block btn btn-primary d-block mx-2`}
                                      type="button"
                                      onClick={handlePrevStep}>
                                      Back to Contacts
                                  </button>
                                  <button
                                      disabled={!prefsSaved}
                                      className={`btn btn-primary d-block mx-2`}
                                      type="button"
                                      onClick={handleNextStep}>
                                      Complete Registration
                                  </button>
                              </div>
                          </>
                      )
                          ;
                  case 4:
                      console.log('Contacts at completion:', contacts);
                      return (
                          <div className={`mx-auto`} style={{width: '90%', maxWidth: '700px'}}>
                              <div className={'d-flex justify-content-center align-items-center'}>
                                  <div className="" style={{fontSize: '64px'}}>🎉</div>
                                  <div className={'pt-3'}>
                                      <h2 className="fw-bold mb-0">Welcome to User X Support!</h2>
                                      <p className="fs-5 ps-1">We're thrilled to have <strong>{companyInfo.name}</strong> on
                                          board.</p>
                                  </div>
                              </div>
                              <div className={'d-flex gap-5'}>
                                  <div className={'w-50'}>
                                      <p>From website tweaks to major updates, from security concerns to performance
                                          optimization —
                                          our support platform is here to keep your business moving forward. Whether you
                                          need a quick fix
                                          or long-term strategy, just open a ticket and we’ll be right there with
                                          you.</p>
                                      <p>To get the ball rolling we will have a <strong>kickoff meeting</strong> to
                                          securely exchange FTP/WordPress access. This meeting ensures we can start
                                          supporting your site right away.</p>

                                  </div>
                                  <div>
                                      <h4>What’s next?</h4>
                                      <ul className="list-unstyled mt-2 small">
                                          <li className={'mb-2'}>✅ Check your inbox to complete account setup</li>
                                          <li className={'mb-2'}>✅ Log in to view your support dashboard</li>
                                          <li className={'mb-2'}>✅ Submit ticket for <strong>kickoff meeting</strong></li>
                                          <li className={'mb-2'}>✅ Gather FTP & WordPress access for meeting</li>
                                          <li className={'mb-2'}>✅ <strong><a
                                              href='https://www.whoisuserx.com/blog/contact-us/' target='_blank'>Schedule a
                                              kickoff meeting</a></strong></li>
                                      </ul>
                                      <p>We are looking forward to working with you and your company!</p>
                                  </div>

                              </div>

                          </div>
                      );
                  default:
                      return null;
              }
          };

          return (
              <div className={`py-2 px-3 m-3 mx-auto`} style={{width: '90%', maxWidth: '1180px'}}>
                  <h2 className={`text-center`}>Register for User X Support</h2>
                  <h4 className={`text-center`} style={{color: '#999'}}>
                      <span className={step === 1 ? `text-dark` : ''}>Company Info</span>
                      &nbsp;&nbsp;<i className={`bi bi-arrow-right`}></i>&nbsp;&nbsp;
                      <span className={step === 2 ? `text-dark` : ''}>Contacts</span>
                      &nbsp;&nbsp;<i className={`bi bi-arrow-right`}></i>&nbsp;&nbsp;
                      <span className={step === 3 ? `text-dark` : ''}>Preferences</span>
                      &nbsp;&nbsp;<i className={`bi bi-arrow-right`}></i>&nbsp;&nbsp;
                      <span className={step === 4 ? `text-dark` : ''}>Welcome</span>
                  </h4>
                  <hr/>
                  {errorMsg !== '' ? <div className={`alert alert-danger`} role={`alert`}>{errorMsg}</div> : null}
                  {renderStep()}
              </div>
          );
      }
;

export default RegistrationForm;
